import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { sacredPath, displayName, editPath } from "@/lib/sacred-library/utils/registry";
import { useSacredEntry } from "@/hooks/useSacredEntry";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import { SacredTopicImage } from "./SacredTopicImage";
import {
  BeginnerDeityView,
  BeginnerFestivalView,
  BeginnerVrataView,
  BeginnerStoryView,
  BeginnerSadhanaView,
  BeginnerMantraView,
} from "./BeginnerDeepView";
import { Loader2 } from "lucide-react";
import { TattvaConnections } from "@/components/tattva/TattvaConnections";

const TYPE_LABELS = {
  deity: "Deity",
  festival: "Festival",
  vrata: "Vrata",
  story: "Story",
  sadhana: "Sādhana",
  mantra: "Mantra",
};

function RelatedTopics({ related }) {
  if (!related?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 pt-4 border-t mt-4">
      <span className="text-xs text-muted-foreground w-full">Related topics</span>
      {related.map(({ type, id }) => (
        <Button key={`${type}-${id}`} variant="outline" size="sm" asChild>
          <Link to={sacredPath(type, id)} className="capitalize">
            {id.replace(/_/g, " ")}
          </Link>
        </Button>
      ))}
    </div>
  );
}

export function SacredTopicPage({ topicType, token }) {
  const { id } = useParams();
  const { entry, loading, related } = useSacredEntry(topicType, id);
  const { canEditLibrary } = useSacredAdmin();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="min-h-screen py-24 px-4 text-center">
        <p className="text-muted-foreground mb-4">Topic not found in Sacred Library.</p>
        <Button asChild variant="outline">
          <Link to="/library">Back to Library</Link>
        </Button>
      </div>
    );
  }

  const title = displayName(topicType, entry);
  const editUrl = editPath(topicType, id);

  let body = null;
  if (topicType === "deity") body = <BeginnerDeityView entry={entry} token={token} />;
  else if (topicType === "festival") body = <BeginnerFestivalView entry={entry} token={token} />;
  else if (topicType === "vrata") body = <BeginnerVrataView entry={entry} token={token} />;
  else if (topicType === "story") body = <BeginnerStoryView entry={entry} token={token} />;
  else if (topicType === "sadhana") body = <BeginnerSadhanaView entry={entry} token={token} />;
  else if (topicType === "mantra") body = <BeginnerMantraView entry={entry} token={token} />;

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid={`sacred-topic-${topicType}-${id}`}>
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/library">
              <ArrowLeft className="w-4 h-4 mr-1" /> Sacred Library
            </Link>
          </Button>
          {canEditLibrary && topicType !== "mantra" && (
            <Button size="sm" variant="default" asChild data-testid="sacred-edit-btn">
              <Link to={editUrl}>
                <Pencil className="w-4 h-4 mr-1" /> Edit topic
              </Link>
            </Button>
          )}
        </div>
        <Card className="bg-white/90 shadow-card overflow-hidden">
          <div className="pt-6 px-6">
            <SacredTopicImage
              type={topicType}
              src={entry.image_url}
              alt={title}
              size="detail"
              className="mx-auto mb-4 max-w-[320px] w-full"
              testId="sacred-topic-hero-image"
            />
          </div>
          <CardHeader className="pt-0">
            <Badge variant="outline" className="w-fit mb-2">
              {TYPE_LABELS[topicType]}
            </Badge>
            <CardTitle className="font-heading text-3xl">{title}</CardTitle>
            <p className="text-sm text-muted-foreground">
              Beginner layer shown first — expand sections below for deep study.
            </p>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">{body}</CardContent>
          <RelatedTopics related={related} />
          <div className="px-6 pb-6">
            <TattvaConnections topicType={topicType} topicId={id} />
          </div>
        </Card>
      </div>
    </div>
  );
}
